import React, { useEffect, useMemo, useState } from 'react';
import { CartLine, CatalogProduct } from '../newOrderTypes';
import { SearchInput } from '../../shared';
import { useSkusStore } from '../../../../store/skusStore';
import { useCategoriesStore } from '../../../../store/categoriesStore';
import { Sku } from '../../../../services/skusApi';
import styles from './ProductsStep.module.css';

interface ProductsStepProps {
  cart: CartLine[];
  setCart: React.Dispatch<React.SetStateAction<CartLine[]>>;
}

export function adapt(s: Sku): CatalogProduct {
  return {
    id: String(s.id),
    name: s.product_name,
    category: s.category_name || 'Other',
    price: `$${(s.list_price ?? s.unit_cost).toFixed(2)}`,
    stock: `${s.total_stock} in stock`,
    rawPrice: s.list_price ?? s.unit_cost,
    skuId: s.id,
  };
}

export const ProductsStep: React.FC<ProductsStepProps> = ({ cart, setCart }) => {
  const [activeCat, setActiveCat] = useState<string>('all');
  const [search, setSearch] = useState('');

  const skus = useSkusStore((s) => s.items);
  const fetchSkus = useSkusStore((s) => s.fetchList);
  const setSkuFilters = useSkusStore((s) => s.setFilters);
  const categories = useCategoriesStore((s) => s.items);
  const fetchCategories = useCategoriesStore((s) => s.fetchList);

  useEffect(() => {
    setSkuFilters({ status: 'active', in_stock_only: true });
    fetchSkus();
    fetchCategories();
  }, [setSkuFilters, fetchSkus, fetchCategories]);

  const adapted = useMemo<CatalogProduct[]>(() => skus.map(adapt), [skus]);

  const filtered = useMemo(() => {
    return adapted.filter((p) => {
      const matchesCategory = activeCat === 'all' || p.category === activeCat;
      const matchesSearch =
        !search.trim() || p.name.toLowerCase().includes(search.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [adapted, activeCat, search]);

  const qtyOf = (id: string) => cart.find((c) => c.product.id === id)?.qty ?? 0;

  const setQty = (id: string, delta: number) => {
    setCart((prev) => {
      const existing = prev.find((c) => c.product.id === id);
      if (!existing) {
        const product = adapted.find((p) => p.id === id);
        if (!product) return prev;
        return [...prev, { product, qty: 1 }];
      }
      return prev
        .map((c) => (c.product.id === id ? { ...c, qty: Math.max(0, c.qty + delta) } : c))
        .filter((c) => c.qty > 0);
    });
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.scanBox}>
        <div className={styles.scanIcon}>
          <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
            <path d="M9.5 6.5v3h-3v-3h3M11 5H5v6h6V5zm-1.5 9.5v3h-3v-3h3M11 13H5v6h6v-6zm6.5-6.5v3h-3v-3h3M19 5h-6v6h6V5zm-6 8h1.5v1.5H13V13zm1.5 1.5H16V16h-1.5v-1.5zM16 13h1.5v1.5H16V13zm-3 3h1.5v1.5H13V16zm1.5 1.5H16V19h-1.5v-1.5zM16 16h1.5v1.5H16V16zm1.5-1.5H19V16h-1.5v-1.5zm0 3H19V19h-1.5v-1.5z" />
          </svg>
        </div>
        <p className={styles.scanTitle}>Tap to scan UPC of Product</p>
        <p className={styles.scanSub}>Point device camera at barcode</p>
        <p className={styles.orManual}>or enter manually</p>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>📦 Select Products</h3>

        <p className={styles.label}>Category</p>
        <div className={styles.categoryRow}>
          <button
            key="all"
            className={`${styles.categoryPill} ${activeCat === 'all' ? styles.categoryPillActive : ''}`}
            onClick={() => setActiveCat('all')}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              className={`${styles.categoryPill} ${activeCat === cat.name ? styles.categoryPillActive : ''}`}
              onClick={() => setActiveCat(cat.name)}
            >
              {cat.name}
            </button>
          ))}
        </div>

        <div className={styles.searchWrap}>
          <SearchInput
            placeholder="Search products in this category..."
            value={search}
            onChange={setSearch}
          />
        </div>

        <div className={styles.products}>
          {filtered.map((product) => {
            const q = qtyOf(product.id);
            return (
              <div key={product.id} className={styles.product}>
                <div className={styles.productInfo}>
                  <p className={styles.productName}>{product.name}</p>
                  <p className={styles.productCategory}>{product.category}</p>
                  <p className={styles.productPrice}>
                    {product.price} <span className={styles.stockText}>{product.stock}</span>
                  </p>
                </div>
                {q > 0 ? (
                  <div className={styles.qtyControl}>
                    <button onClick={() => setQty(product.id, -1)}>-</button>
                    <span className={styles.qtyValue}>{q}</span>
                    <button onClick={() => setQty(product.id, 1)}>+</button>
                  </div>
                ) : (
                  <button className={styles.addBtn} onClick={() => setQty(product.id, 1)}>+ Add</button>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <p style={{ color: '#6B7280', fontSize: 14, padding: 12 }}>No products in this category yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};
